import { SectionHeader } from "@/components/RevenueEngine";
import { motion } from "framer-motion";

const OUTCOMES = [
  {
    label: "Qualified meetings",
    metric: "Weekly",
    desc: "Booked directly on your reps' calendars — accounts showing active intent, not cold lists.",
    color: "#CCFF00",
    size: "col-span-12 md:col-span-7 row-span-1",
    tag: "Delivery",
  },
  {
    label: "A full RevOps function",
    metric: "Automated",
    desc: "Running 24/7 without you lifting a finger.",
    color: "#D946EF",
    size: "col-span-12 md:col-span-5 row-span-1",
    tag: "Operations",
  },
  {
    label: "You'll know first.",
    metric: "Real-time",
    desc: "The moment an account shows intent, your rep knows. Before your competition even notices.",
    color: "#FFB800",
    size: "col-span-12 md:col-span-5",
    tag: "Intelligence",
  },
  {
    label: "Full visibility",
    metric: "Always on",
    desc: "Every signal caught, every action logged, every meeting tracked. You see exactly what the system is doing, in real time.",
    color: "#00F0FF",
    size: "col-span-12 md:col-span-7",
    tag: "Visibility",
  },
];

export default function WhatYouGet() {
  return (
    <section
      id="output"
      data-testid="what-you-get"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="absolute inset-0 precision-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="Output / 06"
          title={<>The output.<br /><span className="text-[#CCFF00]">Not the effort.</span></>}
          sub="We don't sell retainers, slide decks, or campaigns. We sell outcomes wired into your revenue stack."
        />

        <div className="mt-16 grid grid-cols-12 grid-rows-2 gap-4 md:gap-6">
          {OUTCOMES.map((o, i) => (
            <motion.div
              key={o.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`${o.size} relative border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-7 md:p-10 group overflow-hidden`}
              data-testid={`outcome-card-${i}`}
              style={{ boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.04)" }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: o.color, boxShadow: `0 0 20px ${o.color}` }} />
              <div className="absolute top-0 right-0 px-3 py-1.5 border-l border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {o.tag}
              </div>
              <div className="mt-8 flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {o.label}
                  </div>
                  <div className="mt-3 font-display font-bold uppercase tracking-[-0.04em] text-5xl md:text-6xl lg:text-7xl leading-[0.9]" style={{ color: o.color }}>
                    {o.metric}
                  </div>
                  <p className="mt-5 font-body text-zinc-300 text-[14px] md:text-[15px] leading-relaxed max-w-[44ch]">
                    {o.desc}
                  </p>
                </div>
                <div className="opacity-0 group-hover:opacity-60 transition-opacity">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path d="M20 6v28M6 20h28" stroke={o.color} strokeWidth="1.5" />
                    <circle cx="20" cy="20" r="3" fill={o.color} />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
